import { useCallback, useState } from 'react';

const DISMISSED_KEY = 'alerts-dismissed';
const LAST_SEEN_KEY = 'alerts-last-seen';
const MAX_STORED = 2000;

function loadDismissed(): Set<string> {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
  } catch {
    return new Set();
  }
}

function saveDismissed(ids: Set<string>) {
  const pruned = [...ids].sort((a, b) => a.localeCompare(b)).slice(-MAX_STORED);
  localStorage.setItem(DISMISSED_KEY, JSON.stringify(pruned));
  return new Set(pruned);
}

function loadLastSeen(): Date {
  try {
    const raw = localStorage.getItem(LAST_SEEN_KEY);
    return raw ? new Date(raw) : new Date(0);
  } catch {
    return new Date(0);
  }
}

export function useAlertsRead() {
  const [dismissed, setDismissed] = useState<Set<string>>(loadDismissed);

  const dismiss = useCallback((ids: string[]) => {
    setDismissed((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return saveDismissed(next);
    });
  }, []);

  return { dismissed, dismiss };
}

export function useAlertsLastSeen() {
  const [lastSeen, setLastSeen] = useState<Date>(loadLastSeen);

  const markSeen = useCallback(() => {
    const now = new Date();
    localStorage.setItem(LAST_SEEN_KEY, now.toISOString());
    setLastSeen(now);
  }, []);

  return { lastSeen, markSeen };
}
